/**
 * inventory.js — Asset inventory domain service.
 * Inventory lists, detail, exclusion toggle, sites, org selectors, scanner lookups.
 */
import {
  toApiUrl,
  toSameOriginApiIfProxied,
  fetchWithAuthRetry,
  jsonFetchOptions,
  parseJsonOrThrow,
  readApiErrorMessage,
  handleUnauthorizedResponse,
  normalizeApiList,
  isEndpointUnavailable,
  setEndpointUnavailable,
  SITES_ENDPOINT_UNAVAILABLE_KEY,
  NET_SCANNERS_ENDPOINT_UNAVAILABLE_KEY,
  WEB_SCANNERS_ENDPOINT_UNAVAILABLE_KEY,
  INVENTORY_DETAIL_ENDPOINT_UNAVAILABLE_KEY,
} from './_core';
import { listOpenvasConnectors } from './connector.service';
import { listOrganizationsAll } from './user.service';
import { getNetworkScanHistory, getNetworkScanSchedule } from './scan.service';

// — Private inventory helpers —

function parseHostnameFromUrl(rawUrl) {
  const value = String(rawUrl || '').trim();
  if (!value) return '';
  try {
    return new URL(value).hostname || value;
  } catch {
    return value;
  }
}

function toNumberOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function normalizeSiteName(site) {
  return (
    site?.site_name ||
    site?.name ||
    site?.label ||
    site?.display_name ||
    site?.org_name ||
    'Default'
  );
}

function normalizeSiteUuid(site) {
  return (
    site?.uu_id ||
    site?.site_uuid ||
    site?.uuid ||
    site?.id ||
    null
  );
}

async function fetchSiteInventory(siteUuid, inventoryType, token = null) {
  const safeSiteUuid = String(siteUuid || '').trim();
  if (!safeSiteUuid) return [];

  const response = await fetchWithAuthRetry(
    toApiUrl(`/dashboard/sites/${encodeURIComponent(safeSiteUuid)}/inventories/${inventoryType}/`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    if (response.status === 401) {
      await handleUnauthorizedResponse(response);
    }
    if (response.status === 404) {
      return [];
    }
    const msg = await readApiErrorMessage(
      response,
      `${inventoryType} inventory fetch failed (${response.status})`
    );
    throw new Error(msg);
  }

  const data = await parseJsonOrThrow(response, `${inventoryType} inventory`);
  return normalizeApiList(data);
}

function normalizeLookupText(value) {
  return String(value || '').trim().toLowerCase();
}

function makeInventoryLookupKey(ip, hostname, siteName) {
  return [normalizeLookupText(ip), normalizeLookupText(hostname), normalizeLookupText(siteName)].join('|');
}

async function fetchInventoryIdLookup(token = null, query = null) {
  try {
    const qs = `?${new URLSearchParams({
      ...(query && typeof query === 'object' ? query : {}),
      view: 'lookup',
    }).toString()}`;
    const response = await fetchWithAuthRetry(toApiUrl(`/inventory/${qs}`), jsonFetchOptions(token), token);
    if (!response.ok) return new Map();

    const data = await parseJsonOrThrow(response, 'Inventory list');
    const rows = normalizeApiList(data);
    const lookup = new Map();

    rows.forEach((item) => {
      const resolvedId = item?.uu_id || item?.inventory_uuid || item?.inventory_id || item?.id || null;
      if (!resolvedId) return;

      const metadata = {
        id: resolvedId,
        is_excluded: Boolean(item?.is_excluded),
        excluded_reason: item?.excluded_reason || '',
        mac_address: item?.mac_address || item?.macAddress || item?.mac || '',
        mac_vendor: item?.mac_vendor || item?.macVendor || '',
      };

      const siteName = item?.site_name || item?.site?.site_name || item?.site?.name || '';
      const ip = item?.ip || item?.host || '';
      const hostname = item?.hostname || '';

      const fullKey = makeInventoryLookupKey(ip, hostname, siteName);
      const ipOnlyKey = makeInventoryLookupKey(ip, '', siteName);

      if (ip || hostname || siteName) {
        if (fullKey !== '||' && !lookup.has(fullKey)) lookup.set(fullKey, metadata);
        if (ipOnlyKey !== '||' && !lookup.has(ipOnlyKey)) lookup.set(ipOnlyKey, metadata);
      }
    });

    return lookup;
  } catch {
    return new Map();
  }
}

function resolveInventoryOrgName(item, fallbackOrgName = '') {
  return (
    item?.org_name
    || item?.organization_name
    || (typeof item?.organization === 'string' ? item.organization : item?.organization?.name)
    || fallbackOrgName
    || ''
  );
}

function mapNetworkInventoryRows(rows, siteName, siteUuid, inventoryIdLookup = null, orgName = '') {
  return rows.map((item) => {
    const deployment = String(item?.scanner_deployment || '').toLowerCase();
    const onPrem = deployment.includes('on_prem');
    const ip = item?.ip || item?.host || '';
    const hostname = item?.hostname || '';
    const byExactLookup = inventoryIdLookup?.get(makeInventoryLookupKey(ip, hostname, siteName));
    const byIpLookup = inventoryIdLookup?.get(makeInventoryLookupKey(ip, '', siteName));
    const resolvedId = byExactLookup?.id || byIpLookup?.id || item?.uu_id || item?.inventory_uuid || item?.inventory_id || item?.id || null;
    const macAddress =
      item?.mac_address ||
      item?.macAddress ||
      item?.mac ||
      byExactLookup?.mac_address ||
      byIpLookup?.mac_address ||
      '';
    const macVendor =
      item?.mac_vendor ||
      item?.macVendor ||
      byExactLookup?.mac_vendor ||
      byIpLookup?.mac_vendor ||
      '';

    return {
      uu_id: resolvedId,
      inventory_uuid: item?.inventory_uuid || item?.uu_id || null,
      inventory_id: item?.inventory_id || item?.id || null,
      ip,
      hostname,
      org_name: resolveInventoryOrgName(item, orgName),
      site_name: item?.site_name || siteName || 'Default',
      site_uuid: item?.site_uuid || siteUuid || null,
      device_type: onPrem ? 'On-Premise' : 'Perimeter',
      mac_address: macAddress,
      certificate_count: toNumberOrZero(item?.certificate_count),
      total_vul: toNumberOrZero(item?.total_vul),
      on_prem: onPrem,
      webapp: false,
      host_os: item?.host_os || '',
      host_software: item?.host_software || '',
      mac_vendor: macVendor,
      last_scan_date: item?.last_scan_date || item?.date_time || '',
      is_excluded: Boolean(item?.is_excluded ?? byExactLookup?.is_excluded ?? byIpLookup?.is_excluded),
      excluded_reason: item?.excluded_reason || byExactLookup?.excluded_reason || byIpLookup?.excluded_reason || '',
      open_ports: item?.open_ports || '',
      certificates: item?.certificates || [],
    };
  });
}

function mapWebInventoryRows(rows, siteName, inventoryIdLookup = null, orgName = '') {
  return rows.map((item) => {
    const scanUrl = String(item?.scan_url || '').trim();
    const ip = item?.ip || '';
    const hostname = item?.hostname || parseHostnameFromUrl(scanUrl);
    const byExactLookup = inventoryIdLookup?.get(makeInventoryLookupKey(ip, hostname, siteName));
    const byIpLookup = inventoryIdLookup?.get(makeInventoryLookupKey(ip, '', siteName));
    const resolvedId =
      byExactLookup?.id ||
      byIpLookup?.id ||
      item?.inventory_uuid ||
      item?.uu_id ||
      item?.scan_id ||
      item?.inventory_id ||
      item?.id ||
      null;
    return {
      uu_id: resolvedId,
      inventory_uuid: item?.inventory_uuid || null,
      inventory_id: item?.inventory_id || item?.id || null,
      ip,
      hostname,
      org_name: resolveInventoryOrgName(item, orgName),
      site_name: item?.site_name || siteName || 'Default',
      site_uuid: item?.site_uuid || null,
      device_type: 'Web App',
      mac_address: '',
      certificate_count: toNumberOrZero(item?.certificate_count),
      total_vul: toNumberOrZero(item?.total_vul),
      on_prem: false,
      webapp: true,
      host_os: '',
      host_software: '',
      mac_vendor: '',
      last_scan_date: item?.last_scan_date || item?.date_time || '',
      is_excluded: Boolean(item?.is_excluded ?? byExactLookup?.is_excluded ?? byIpLookup?.is_excluded),
      excluded_reason: item?.excluded_reason || byExactLookup?.excluded_reason || byIpLookup?.excluded_reason || '',
      open_ports: item?.open_ports || '',
      certificates: item?.certificates || [],
      scan_url: scanUrl,
    };
  });
}

async function fetchSitesFromInventory(token = null) {
  try {
    const response = await fetchWithAuthRetry(toApiUrl('/inventory/?view=lookup'), jsonFetchOptions(token), token);
    if (!response.ok) {
      return [];
    }

    const data = await parseJsonOrThrow(response, 'Inventory list');
    const rows = normalizeApiList(data);
    const seen = new Set();
    const sites = [];

    rows.forEach((item) => {
      const siteUuid = item?.site_uuid || item?.site?.uu_id || item?.site_uu_id || null;
      if (!siteUuid) return;

      const key = String(siteUuid).trim();
      if (!key || seen.has(key)) return;
      seen.add(key);

      sites.push({
        uu_id: key,
        site_name: item?.site_name || item?.site?.site_name || item?.site?.name || 'Default',
      });
    });

    return sites;
  } catch {
    return [];
  }
}

function mergeOrganizationsFromSites(orgRows, sites) {
  const byKey = new Map();
  const keyForOrg = (row) => {
    const u = String(row?.uu_id ?? row?.org_uuid ?? row?.organization_uuid ?? '').trim().toLowerCase();
    if (u) return `uuid:${u}`;
    const id = String(row?.id ?? row?.organization_id ?? row?.org_id ?? '').trim().toLowerCase();
    if (id) return `id:${id}`;
    const n = String(row?.name ?? row?.org_name ?? '').trim().toLowerCase();
    return n ? `name:${n}` : '';
  };

  (Array.isArray(orgRows) ? orgRows : []).forEach((row) => {
    const k = keyForOrg(row);
    if (!k) return;
    byKey.set(k, { ...row });
  });

  (Array.isArray(sites) ? sites : []).forEach((s) => {
    const uuidRaw = s?.org_uuid ?? s?.organization_uuid;
    const uuid = String(uuidRaw ?? '').trim().toLowerCase();
    const name = String(s?.organization ?? '').trim();
    if (!uuid && !name) return;
    const k = uuid ? `uuid:${uuid}` : `name:${name.toLowerCase()}`;
    if (byKey.has(k)) return;
    byKey.set(k, {
      id: uuidRaw ?? name,
      uu_id: uuidRaw ?? null,
      name: name || 'Organization',
    });
  });

  return Array.from(byKey.values());
}

// — Exported functions —

function enrichInventoryRowsWithLookup(rows, inventoryIdLookup) {
  if (!inventoryIdLookup?.size || !Array.isArray(rows) || rows.length === 0) {
    return Array.isArray(rows) ? rows : [];
  }

  return rows.map((row) => {
    const siteName = row?.site_name || '';
    const ip = row?.ip || '';
    const hostname = row?.hostname || '';
    const byExactLookup = inventoryIdLookup.get(makeInventoryLookupKey(ip, hostname, siteName));
    const byIpLookup = inventoryIdLookup.get(makeInventoryLookupKey(ip, '', siteName));
    const meta = byExactLookup || byIpLookup;
    if (!meta) return row;

    return {
      ...row,
      uu_id: meta.id || row.uu_id || null,
      mac_address: row.mac_address || meta.mac_address || '',
      mac_vendor: row.mac_vendor || meta.mac_vendor || '',
      is_excluded: Boolean(row.is_excluded || meta.is_excluded),
      excluded_reason: row.excluded_reason || meta.excluded_reason || '',
    };
  });
}

function mapInventoryTableRow(item) {
  return {
    ...item,
    inventory_uuid: item?.uu_id || null,
    inventory_id: item?.uu_id || null,
    device_type: item?.webapp ? 'Web App' : (item?.on_prem ? 'On-Premise' : 'Perimeter'),
    webapp: Boolean(item?.webapp),
    on_prem: Boolean(item?.on_prem),
    certificates: [],
  };
}

function isConcreteHostTarget(raw) {
  const target = String(raw || '').trim();
  if (!target || target.toUpperCase() === 'NA') return false;
  if (target.includes('/') || target.includes(',') || /\s/.test(target)) return false;
  return true;
}

function inventoryHostKey(ip, siteUuid, siteName) {
  return `${String(ip || '').trim().toLowerCase()}|${String(siteUuid || '').trim().toLowerCase()}|${String(siteName || '').trim().toLowerCase()}`;
}

/**
 * Assets should include every concrete host known for the org/site — including
 * network scan schedule targets that are not yet in InventoryDb (e.g. zero-finding scans).
 */
async function mergeKnownNetworkHostsIntoInventory(rows, token = null, query = null) {
  const base = Array.isArray(rows) ? rows.slice() : [];
  const [scheduleResult, historyResult, sitesResult] = await Promise.allSettled([
    getNetworkScanSchedule(token, query),
    getNetworkScanHistory(token, query),
    getSites(token, query),
  ]);
  const schedules =
    scheduleResult.status === 'fulfilled' && Array.isArray(scheduleResult.value)
      ? scheduleResult.value
      : [];
  const history =
    historyResult.status === 'fulfilled' && Array.isArray(historyResult.value)
      ? historyResult.value
      : [];
  const sites =
    sitesResult.status === 'fulfilled' && Array.isArray(sitesResult.value)
      ? sitesResult.value
      : [];

  // Schedule API omits org_name — resolve it from inventory rows, history, or sites.
  const orgBySiteUuid = new Map();
  const rememberOrg = (siteUuid, orgName) => {
    const key = String(siteUuid || '').trim().toLowerCase();
    const name = String(orgName || '').trim();
    if (!key || !name || orgBySiteUuid.has(key)) return;
    orgBySiteUuid.set(key, name);
  };
  base.forEach((row) => {
    rememberOrg(
      row?.site_uuid || row?.site_id,
      row?.org_name || row?.organization_name
    );
  });
  history.forEach((row) => {
    rememberOrg(
      row?.site_uuid || row?.site_uu_id,
      row?.org_name || row?.organization_name
    );
  });
  sites.forEach((site) => {
    rememberOrg(
      site?.uu_id || site?.site_uuid || site?.uuid,
      site?.organization_name
        || (typeof site?.organization === 'string' ? site.organization : site?.organization?.name)
        || site?.org_name
    );
  });

  const knownHosts = [
    ...schedules.map((row) => ({ ...row, _assetSource: 'schedule' })),
    ...history.map((row) => ({ ...row, _assetSource: 'scan-history' })),
  ];
  if (knownHosts.length === 0) return base;

  const seen = new Set(
    base.map((row) =>
      inventoryHostKey(row?.ip, row?.site_uuid || row?.site_id, row?.site_name || row?.site)
    )
  );

  for (let i = 0; i < knownHosts.length; i += 1) {
    const knownHost = knownHosts[i];
    const ip = String(
      knownHost?.target
      || knownHost?.endpoint
      || knownHost?.ip
      || knownHost?.scan_ip
      || ''
    ).trim();
    if (!isConcreteHostTarget(ip)) continue;

    const siteUuid = String(knownHost?.site_uuid || knownHost?.site_uu_id || '').trim();
    const siteName = String(knownHost?.site_name || knownHost?.site || '').trim() || 'Default';
    const key = inventoryHostKey(ip, siteUuid, siteName);
    if (seen.has(key)) continue;
    seen.add(key);

    const onPrem = String(knownHost?.scanner_deployment || '').toLowerCase() === 'on_premise';
    const sourceId = String(
      knownHost?.uu_id
      || knownHost?.scan_id
      || knownHost?.id
      || `${siteUuid}-${ip}`
    ).trim();
    const orgName =
      String(knownHost?.org_name || knownHost?.organization_name || '').trim()
      || orgBySiteUuid.get(siteUuid.toLowerCase())
      || '';
    base.push(
      mapInventoryTableRow({
        uu_id: null,
        inventory_uuid: null,
        inventory_id: null,
        ip,
        hostname: 'NA',
        site_name: siteName,
        site_uuid: siteUuid || null,
        organization_name: orgName,
        org_name: orgName,
        on_prem: onPrem,
        webapp: false,
        is_excluded: false,
        is_active: true,
        total_vul: 0,
        critical_vul: 0,
        high_vul: 0,
        medium_vul: 0,
        low_vul: 0,
        last_scan_date: knownHost?.date_time || knownHost?.last_scan_date || null,
        open_ports: '',
        host_os: '',
        host_software: '',
        mac_address: '',
        mac_vendor: '',
        // Synthetic marker so UI can treat exclude/delete as unavailable.
        detail_source: knownHost._assetSource,
        schedule_uuid: sourceId,
      })
    );
  }

  return base;
}

/**
 * Load inventory across sites.
 * options.onPartial(rows) may be called as each site finishes (before MAC/exclusion enrichment).
 *
 * The `/inventory/` API paginates by default (page_size=50). This loader follows `next`
 * (or page numbers) so All Organizations is not silently truncated to the first page.
 */
export async function getInventory(token = null, query = null, options = null) {
  const onPartial = typeof options?.onPartial === 'function' ? options.onPartial : null;

  // Preferred path: a single lightweight, organization-scoped inventory request.
  // Keep the legacy fan-out below as a compatibility fallback for older servers.
  try {
    const baseQuery = {
      ...(query && typeof query === 'object' ? query : {}),
      view: 'table',
      page_size: '200',
      page: '1',
    };
    const params = new URLSearchParams(baseQuery);
    const response = await fetchWithAuthRetry(
      toApiUrl(`/inventory/?${params.toString()}`),
      jsonFetchOptions(token),
      token
    );
    if (response.ok) {
      const firstData = await parseJsonOrThrow(response, 'Inventory table');
      const merged = normalizeApiList(firstData).map(mapInventoryTableRow);
      if (onPartial) onPartial(merged);

      const isPaginated =
        Array.isArray(firstData?.results)
        || typeof firstData?.next === 'string'
        || Number(firstData?.total_pages) > 1
        || Number(firstData?.count) > merged.length;

      if (!isPaginated) {
        const withSchedules = await mergeKnownNetworkHostsIntoInventory(merged, token, query);
        if (onPartial) onPartial(withSchedules);
        return withSchedules;
      }

      const seenKeys = new Set(
        merged.map((row, idx) => String(row?.uu_id || row?.inventory_uuid || `${idx}`))
      );
      let nextUrl = toSameOriginApiIfProxied(
        typeof firstData?.next === 'string' && firstData.next.trim() ? firstData.next.trim() : ''
      );
      if (!nextUrl) nextUrl = null;

      let page = Math.max(1, Number(firstData?.current_page) || 1);
      const totalPages = Math.max(0, Number(firstData?.total_pages) || 0);
      let safety = 0;

      while (safety < 100) {
        safety += 1;

        if (!nextUrl) {
          if (totalPages && page < totalPages) {
            page += 1;
            const pageParams = new URLSearchParams({
              ...baseQuery,
              page: String(page),
            });
            nextUrl = toApiUrl(`/inventory/?${pageParams.toString()}`);
          } else {
            break;
          }
        }

        const pageResponse = await fetchWithAuthRetry(nextUrl, jsonFetchOptions(token), token);
        if (!pageResponse.ok) break;

        const pageData = await parseJsonOrThrow(pageResponse, 'Inventory table');
        const pageRows = normalizeApiList(pageData).map(mapInventoryTableRow);
        if (pageRows.length === 0) break;

        for (let i = 0; i < pageRows.length; i += 1) {
          const row = pageRows[i];
          const key = String(row?.uu_id || row?.inventory_uuid || `${page}-${i}`);
          if (seenKeys.has(key)) continue;
          seenKeys.add(key);
          merged.push(row);
        }
        if (onPartial) onPartial(merged.slice());

        if (typeof pageData?.next === 'string' && pageData.next.trim()) {
          nextUrl = toSameOriginApiIfProxied(pageData.next.trim());
          page = Math.max(page + 1, Number(pageData?.current_page) || page + 1);
          continue;
        }

        nextUrl = null;
        page = Math.max(page, Number(pageData?.current_page) || page);
        if (totalPages && page < totalPages) continue;
        break;
      }

      const withSchedules = await mergeKnownNetworkHostsIntoInventory(merged, token, query);
      if (onPartial) onPartial(withSchedules);
      return withSchedules;
    }
  } catch {
    // Older deployments may not support view=table yet.
  }

  // Kick off sites + enrichment lookup together (lookup must not block site fan-out).
  const sitesPromise = getSites(token, query);
  const lookupPromise = fetchInventoryIdLookup(token, query);

  const sites = await sitesPromise;
  const normalizedSites = Array.isArray(sites) ? sites : [];

  if (normalizedSites.length === 0) {
    // Still settle lookup so we don't leave a dangling request unhandled.
    await lookupPromise;
    const withSchedules = await mergeKnownNetworkHostsIntoInventory([], token, query);
    if (onPartial) onPartial(withSchedules);
    return withSchedules;
  }

  const rowsBySiteKey = new Map();

  await Promise.all(
    normalizedSites.map(async (site) => {
      const siteUuid = normalizeSiteUuid(site);
      if (!siteUuid) return;

      const siteName = normalizeSiteName(site);
      const orgName =
        site?.organization_name
        || (typeof site?.organization === 'string' ? site.organization : site?.organization?.name)
        || site?.org_name
        || '';
      const [webResult, networkResult] = await Promise.allSettled([
        fetchSiteInventory(siteUuid, 'web', token),
        fetchSiteInventory(siteUuid, 'network', token),
      ]);

      const webRows = webResult.status === 'fulfilled' ? webResult.value : [];
      const networkRows = networkResult.status === 'fulfilled' ? networkResult.value : [];
      const mapped = [
        ...mapWebInventoryRows(webRows, siteName, null, orgName).map((row) => ({
          ...row,
          site_uuid: row.site_uuid || siteUuid || null,
        })),
        ...mapNetworkInventoryRows(networkRows, siteName, siteUuid, null, orgName),
      ];

      rowsBySiteKey.set(String(siteUuid), mapped);
    })
  );

  let rows = [];
  rowsBySiteKey.forEach((siteRows) => {
    for (let i = 0; i < siteRows.length; i += 1) rows.push(siteRows[i]);
  });

  const inventoryIdLookup = await lookupPromise;
  rows = enrichInventoryRowsWithLookup(rows, inventoryIdLookup);
  rows = await mergeKnownNetworkHostsIntoInventory(rows, token, query);
  if (onPartial) onPartial(rows);
  return rows;
}

export async function getInventoryDetail(token = null, query = null) {
  if (isEndpointUnavailable(INVENTORY_DETAIL_ENDPOINT_UNAVAILABLE_KEY)) {
    throw new Error('Asset details are currently unavailable. Please try again later.');
  }
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';

  const response = await fetchWithAuthRetry(toApiUrl(`/inventory/detail/${qs}`), jsonFetchOptions(token), token);

  if (!response.ok) {
    if (response.status === 404) {
      return {
        message: 'Asset details are not available right now.',
        inventory: null,
        certificate_info: [],
        vulnerabilities: [],
      };
    }
    const msg = await readApiErrorMessage(response, `Inventory detail fetch failed (${response.status})`);
    throw new Error(msg);
  }
  setEndpointUnavailable(INVENTORY_DETAIL_ENDPOINT_UNAVAILABLE_KEY, false);
  return parseJsonOrThrow(response, 'Inventory detail');
}

export async function toggleInventoryExclusion(inventoryId, token = null) {
  const id = String(inventoryId || '').trim();
  if (!id) {
    throw new Error('Inventory UUID is required.');
  }

  const response = await fetchWithAuthRetry(
    toApiUrl(`/inventory/toggle-exclusion/${encodeURIComponent(id)}/`),
    jsonFetchOptions(token, { method: 'POST' }),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Inventory exclusion toggle failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'Inventory exclusion toggle');
}

export async function deleteInventoryAsset(inventoryId, token = null) {
  const id = String(inventoryId || '').trim();
  if (!id) {
    throw new Error('Inventory UUID is required.');
  }

  const response = await fetchWithAuthRetry(
    toApiUrl(`/inventory/delete/${encodeURIComponent(id)}/`),
    jsonFetchOptions(token, { method: 'POST' }),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Inventory delete failed (${response.status})`);
    throw new Error(msg);
  }

  if (response.status === 204) return { status: 'success' };
  try {
    return await parseJsonOrThrow(response, 'Inventory delete');
  } catch {
    return { status: 'success' };
  }
}

function filterSitesByOrgQuery(sites, query) {
  const rows = Array.isArray(sites) ? sites : [];
  const orgUuid = String(
    query && typeof query === 'object'
      ? (query.org_uu_id || query.organization_uuid || query.org_uuid || '')
      : ''
  ).trim().toLowerCase();
  if (!orgUuid) return rows;

  return rows.filter((site) => {
    const siteOrg = String(
      site?.org_uuid ||
      site?.organization_uuid ||
      site?.organization_uu_id ||
      (typeof site?.organization === 'object'
        ? (site.organization?.uu_id || site.organization?.org_uuid || '')
        : '') ||
      ''
    ).trim().toLowerCase();
    // Inventory fallback rows have no org fields — drop them when an org filter is required.
    if (!siteOrg) return false;
    return siteOrg === orgUuid;
  });
}

export async function getSites(token = null, query = null) {
  if (isEndpointUnavailable(SITES_ENDPOINT_UNAVAILABLE_KEY)) {
    const fallbackSites = await fetchSitesFromInventory(token);
    if (fallbackSites.length > 0) {
      return filterSitesByOrgQuery(fallbackSites, query);
    }
  }
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';

  const response = await fetchWithAuthRetry(toApiUrl(`/site/${qs}`), jsonFetchOptions(token), token);
  if (!response.ok) {
    if (response.status === 404 || response.status >= 500) {
      setEndpointUnavailable(SITES_ENDPOINT_UNAVAILABLE_KEY, true);
      const fallbackSites = await fetchSitesFromInventory(token);
      if (fallbackSites.length > 0) {
        return filterSitesByOrgQuery(fallbackSites, query);
      }
    }
    const msg = await readApiErrorMessage(response, `Sites fetch failed (${response.status})`);
    throw new Error(msg);
  }

  setEndpointUnavailable(SITES_ENDPOINT_UNAVAILABLE_KEY, false);
  const data = await parseJsonOrThrow(response, 'Sites');
  const sites = normalizeApiList(data).filter((site) => {
    const hasUuid = site?.uu_id || site?.site_uuid || site?.uuid || site?.id;
    return !!hasUuid;
  });
  // Backend filters on org_uu_id; enforce the same client-side so ignored params
  // (e.g. legacy org_id) or partial responses cannot leak other orgs' sites.
  return filterSitesByOrgQuery(sites, query);
}

export async function listOrganizationsForSelector(token = null) {
  const orgs = await listOrganizationsAll(token);
  if (orgs.length > 1) return orgs;

  let sites = [];
  try {
    sites = await getSites(token);
  } catch {
    sites = [];
  }
  const merged = mergeOrganizationsFromSites(orgs, sites);
  return merged.length ? merged : orgs;
}

export async function getNetworkScannersForSite(siteUuid, scanType = 'perimeter', token = null, query = null) {
  const site = String(siteUuid || '').trim();
  if (!site) return [];
  if (isEndpointUnavailable(NET_SCANNERS_ENDPOINT_UNAVAILABLE_KEY)) return [];
  const scanTypeValue = String(scanType || 'perimeter').trim().toLowerCase();
  const scanTypeParam = scanTypeValue === 'on-premise' ? 'on_premise' : scanTypeValue;

  const legacyQuery = {
    site_uuid: site,
    scan_type: scanTypeParam,
  };
  if (query && typeof query === 'object' && query.org_id) {
    legacyQuery.org_id = query.org_id;
  }
  const legacyQs = new URLSearchParams(legacyQuery).toString();

  try {
    const legacyResponse = await fetchWithAuthRetry(
      // /api/v1: the root path is not an API route behind the portal and redirects to the app.
      toApiUrl(`/networkscanners/get_network_scanners/?${legacyQs}`),
      jsonFetchOptions(token),
      token
    );

    if (legacyResponse.ok) {
      const legacyRows = normalizeApiList(await parseJsonOrThrow(legacyResponse, 'Network scanners'));
      if (legacyRows.length > 0) return legacyRows;
    }
  } catch {
    // Fall through to connectors API.
  }

  try {
    const connectors = await listOpenvasConnectors(token, query);
    const rows = (Array.isArray(connectors) ? connectors : [])
      .filter((item) => {
        const assignedSites = Array.isArray(item?.assigned_sites) ? item.assigned_sites : [];
        const assignedHere = assignedSites.some((entry) => {
          const uu = String(entry?.uu_id || entry?.site_uuid || entry?.id || '').trim();
          return uu === site;
        });
        if (!assignedHere) return false;
        const isPerimeter = item?.perimeter_scanner === true;
        return scanTypeParam === 'perimeter' ? isPerimeter : !isPerimeter;
      })
      .map((item) => ({
        setting_id: item?.setting_id,
        scanner_name: item?.openvas_name || item?.scanner_name || '',
        scanner_vanity_name: item?.openvas_vanity_name || item?.scanner_vanity_name || item?.openvas_name || '',
        perimeter_scanner: item?.perimeter_scanner === true,
      }))
      .filter((item) => item.setting_id);
    return rows;
  } catch {
    return [];
  }
}

export async function getWebScannersForSite(siteUuid, token = null) {
  const site = String(siteUuid || '').trim();
  if (!site) return [];
  if (isEndpointUnavailable(WEB_SCANNERS_ENDPOINT_UNAVAILABLE_KEY)) return [];

  try {
    const legacyQs = new URLSearchParams({ site_uuid: site }).toString();
    const legacyResponse = await fetchWithAuthRetry(
      toApiUrl(`/webscanners/get_web_scanners/?${legacyQs}`),
      jsonFetchOptions(token),
      token
    );

    if (legacyResponse.ok) {
      const legacyRows = normalizeApiList(await parseJsonOrThrow(legacyResponse, 'Web scanners'));
      return legacyRows;
    }
  } catch {
    return [];
  }
  return [];
}
